'use client';
import { useTranslation } from 'react-i18next';

import Accept from '@icons/Accept.svg';
import Class from '@icons/Class.svg';
import Date from '@icons/Date.svg';
import Decline from '@icons/Decline.svg';
import Email from '@icons/Email.svg';
import Info from '@icons/Info.svg';
import Phone from '@icons/Phone.svg';
import Time from '@icons/Time.svg';
import Whatsapp from '@icons/Whatsapp.svg';
import { ShiftReactionStatus } from 'types';

import { InfoContainer } from '@components/Common';
import { AdressText, TextContainer } from '@components/Listing';
import { ClickableAddress } from '@components/Listing/components/ClickableAddress';
import { ShiftInfoCardBaseProps } from '@components/ShiftInfoCards/interfaces';
import {
  InfoItem,
  SchoolNameText,
  ShiftInfoContainer,
  TimeInfoItem,
} from '@components/ShiftInfoCards/styles';
import { Text } from '@components/TermsCheckbox';

import { formatDisplayDate, formatDisplayTime } from '@utils/helpers';
import { createEmailHref, createPhoneHref, openWhatsApp } from '@utils/helpers/contactHelpers';
import { getTeacherDisplayTranslationKey } from '@utils/shiftFilters';

/**
 * Shift info card with the contact details, reaction info and school name
 */
export const ShiftInfoCard = ({
  shiftId,
  group,
  date,
  timeFrom,
  timeUntil,
  schoolName,
  schoolAddress,
  schoolEnabledWhatsapp,
  contactEmail,
  contactPhone,
  status,
  teacherName,
  substituteTeacherName,
  teacherContext = 'current',
  numberOfReactions,
  onClick,
}: ShiftInfoCardBaseProps) => {
  const { t } = useTranslation('common');

  // Use shared contact helpers instead of duplicate validation logic
  const getEmailHref = () => createEmailHref(contactEmail);
  const getPhoneHref = () => createPhoneHref(contactPhone);
  const handleWhatsappClick = () => openWhatsApp(contactPhone);

  return (
    <ShiftInfoContainer
      $hasInfo={!!schoolAddress}
      key={shiftId}
      onClick={onClick}
      as={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      aria-label={'shift info card'}>
      <InfoContainer>
        {schoolName && <SchoolNameText>{schoolName}</SchoolNameText>}
        <InfoItem>
          <Class />
          <Text>{`Groep ${group}`}</Text>
        </InfoItem>
        <TimeInfoItem>
          <InfoItem>
            <Date />
            <Text>{formatDisplayDate(date)}</Text>
          </InfoItem>
          <InfoItem>
            <Time />
            <Text>{`${formatDisplayTime(timeFrom)} - ${formatDisplayTime(timeUntil)}`}</Text>
          </InfoItem>
        </TimeInfoItem>
        {teacherName && (
          <InfoItem>
            <Accept />
            <Text>
              {(() => {
                const translationKey = getTeacherDisplayTranslationKey(teacherContext);
                return t(translationKey, {
                  substituteName: teacherName,
                });
              })()}
            </Text>
          </InfoItem>
        )}
        {!teacherName && substituteTeacherName && (
          <InfoItem>
            <Accept />
            <Text>
              {t(getTeacherDisplayTranslationKey(teacherContext), {
                substituteName: substituteTeacherName,
              })}
            </Text>
          </InfoItem>
        )}
        {teacherName === undefined && numberOfReactions && (
          <InfoItem>
            <Info />
            <Text>{t('schoolShifts.numberReacted', { number: numberOfReactions })}</Text>
          </InfoItem>
        )}
        {(status === ShiftReactionStatus.NOT_REACTED ||
          status === ShiftReactionStatus.APPLIED ||
          status === ShiftReactionStatus.REJECTED) && (
          <InfoItem>
            {status === ShiftReactionStatus.NOT_REACTED && <Info />}
            {status === ShiftReactionStatus.APPLIED && <Accept />}
            {status === ShiftReactionStatus.REJECTED && <Decline />}
            <Text>
              {status === ShiftReactionStatus.NOT_REACTED
                ? t('teacherShifts.notReacted')
                : status === ShiftReactionStatus.APPLIED
                  ? t('teacherShifts.waitingForSchoolResponse')
                  : t('teacherShifts.rejectedBySchool')}
            </Text>
          </InfoItem>
        )}
      </InfoContainer>
      {schoolAddress && (
        <InfoContainer>
          <SchoolNameText>{t('contact')}</SchoolNameText>
          <ClickableAddress name={schoolName || ''} address={schoolAddress || ''} />
          <TextContainer as="a" href={getEmailHref()}>
            <Email />
            <AdressText>{contactEmail}</AdressText>
          </TextContainer>
          <TimeInfoItem>
            <TextContainer as="a" href={getPhoneHref()}>
              <Phone />
              <AdressText>{contactPhone}</AdressText>
            </TextContainer>
            {schoolEnabledWhatsapp && (
              <TextContainer onClick={handleWhatsappClick} style={{ cursor: 'pointer' }}>
                <Whatsapp />
                <AdressText>{t('whatsapp')}</AdressText>
              </TextContainer>
            )}
          </TimeInfoItem>
        </InfoContainer>
      )}
    </ShiftInfoContainer>
  );
};
